<script setup lang="ts">
import CloseIcon from '~/assets/icon/close.svg'
import DeleteIcon from '~/assets/icon/delete.svg'
import ArrowDownIcon from '~/assets/icon/arrow-down.svg'
import Star from '~/assets/icon/star.svg'
import type { SearchHistoryItem, TraderListItem } from '~/types/home'
import { formatScoreValue } from '~/utils/formatters'

const props = defineProps<{
  searchQuery: string
  hasSearched: boolean
  searchHistory: SearchHistoryItem[]
  popularTraders: TraderListItem[]
}>()

const emit = defineEmits<{
  (e: 'select-history', item: SearchHistoryItem): void
  (e: 'select-trader', item: TraderListItem): void
  (e: 'delete-history', index: number): void
  (e: 'clear-history'): void
}>()

const { t } = useAppI18n()
const matomo = useMatomo()
const tagsContainer = ref<HTMLElement | null>(null)
const isExpanded = ref(false)
const hasMoreThanTwoLines = ref(false)
const maxVisibleIndex = ref(9999)

const displayedHistory = computed(() => props.searchHistory)
const showSearchEmpty = computed(
  () => props.searchQuery.trim() && props.hasSearched && props.popularTraders.length === 0,
)

watch(showSearchEmpty, (val) => {
  if (val) {
    matomo.trackEvent('search_empty', {
      af_content: props.searchQuery,
    })
  }
})

const highlightMatch = (text: string, query: string) => {
  if (!query || !text) return text
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  return text.replace(regex, '<span class="text-[#E2483D]">$1</span>')
}

let checkTimeout: any = null

const checkLines = () => {
  if (!tagsContainer.value) return

  // 临时添加测量 class 以获取完整子元素的真实排布位置
  tagsContainer.value.classList.add('is-measuring')

  const items = tagsContainer.value.querySelectorAll('.tag-item')
  if (items.length === 0) {
    hasMoreThanTwoLines.value = false
    maxVisibleIndex.value = 9999
    tagsContainer.value.classList.remove('is-measuring')
    return
  }

  const firstOffsetTop = (items[0] as HTMLElement).offsetTop
  let thirdLineIndex = -1

  for (let i = 1; i < items.length; i++) {
    const offsetTop = (items[i] as HTMLElement).offsetTop
    // 超过 50px 偏移说明该元素排到了第三行或更低（单行高度约 28px + 行间距 10px = 38px，使用 50px 作为安全判定阈值）
    if (offsetTop - firstOffsetTop > 50) {
      thirdLineIndex = i
      break
    }
  }

  if (thirdLineIndex !== -1) {
    const containerRect = tagsContainer.value.getBoundingClientRect()
    let lastVisibleIndex = thirdLineIndex - 1
    const isRtl = window.getComputedStyle(tagsContainer.value).direction === 'rtl'

    // 回退寻找，确保第二行最后一个显示的元素后面有足够空间放下展开按钮（宽度 30px + gap 8px ≈ 38px，预留 40px）
    while (lastVisibleIndex > 0) {
      const el = items[lastVisibleIndex] as HTMLElement
      const elRect = el.getBoundingClientRect()

      const availableSpace = isRtl ? elRect.left - containerRect.left : containerRect.right - elRect.right

      if (availableSpace >= 40) {
        break
      }
      lastVisibleIndex--
    }

    hasMoreThanTwoLines.value = true
    maxVisibleIndex.value = lastVisibleIndex + 1
  } else {
    hasMoreThanTwoLines.value = false
    maxVisibleIndex.value = 9999
  }

  tagsContainer.value.classList.remove('is-measuring')
}

// 防抖触发，避免 ResizeObserver 触发的 Forced Reflow 发生死循环
const triggerCheck = () => {
  if (checkTimeout) clearTimeout(checkTimeout)
  checkTimeout = setTimeout(() => {
    checkLines()
  }, 60)
}

// 监听数据变化重新计算
watch(
  () => props.searchHistory,
  () => {
    // 数据变化时先重置状态，确保所有标签在测量阶段可见
    // 否则新增标签受旧的 maxVisibleIndex 限制被隐藏，导致 offsetTop 测量不准确
    hasMoreThanTwoLines.value = false
    maxVisibleIndex.value = 9999
    isExpanded.value = false
    nextTick(triggerCheck)
  },
  { deep: true },
)

let resizeObserver: ResizeObserver | null = null

// 监听容器 ref 的变化（v-if 条件切换导致容器 DOM 出现/消失）
// 场景：用户搜索时 searchQuery 非空 → 容器被 v-if 移除 → 搜索完成后 searchHistory 更新
// → 用户清除搜索词 → 容器重新创建 → 此时需要重新计算行数并绑定 ResizeObserver
watch(tagsContainer, (newEl) => {
  // 清理旧 observer（旧 DOM 已被 v-if 销毁，observer 也失效）
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }

  if (newEl) {
    // 容器出现时设置 ResizeObserver 并触发行数计算
    resizeObserver = new ResizeObserver(() => {
      triggerCheck()
    })
    resizeObserver.observe(newEl)
    nextTick(triggerCheck)
  }
})

onMounted(() => {
  window.addEventListener('resize', triggerCheck)
})

onUnmounted(() => {
  if (checkTimeout) clearTimeout(checkTimeout)
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', triggerCheck)
})
const getExchangeStatus = (exchange: TraderListItem) => {
  return exchange?.sortedLabel?.find((item) => item?.labelType === 107)
}
const getExchangeYears = (exchange: TraderListItem) => {
  return exchange?.sortedLabel?.find((item) => item?.labelType === 101)
}

const historyTag = (item: SearchHistoryItem) => {
  emit('select-history', item)
  matomo.trackEvent('search_history', {
    af_content: item.label,
  })
}
const historyDelete = () => {
  emit('clear-history')
  matomo.trackEvent('search_delete', {
    af_source: '首页',
  })
}

const goTraderDetail = (trader: TraderListItem, index: any) => {
  emit('select-trader', trader)
  if (props.searchQuery) {
    matomo.trackEvent('search_related', {
      af_content: props.searchQuery,
      af_name: trader.name,
      af_code: trader.code,
      af_kind: index,
    })
  } else {
    matomo.trackEvent('search_popular', {
      af_name: trader.name,
      af_code: trader.code,
      af_kind: index,
    })
  }
}
</script>

<template>
  <div class="search-dropdown-wrap" @mousedown.prevent>
    <!-- 搜索历史 -->
    <div v-if="!searchQuery && displayedHistory.length > 0" class="section-title">
      <span>{{ t('80356') }}</span>
      <div class="delete-btn" @click="historyDelete">
        <DeleteIcon class="delete-icon" aria-hidden="true" />
      </div>
    </div>
    <div v-if="!searchQuery && displayedHistory.length > 0" ref="tagsContainer" class="history-tags">
      <div
        v-for="(item, index) in displayedHistory"
        v-show="isExpanded || index < maxVisibleIndex"
        :key="index"
        class="tag-item"
        @click="historyTag(item)"
      >
        <CommonImage v-if="item.logo" :src="item.logo" alt="" class="tag-icon" loading="lazy" object-fit="cover" />
        <span>{{ item.label }}</span>
        <CloseIcon class="close-btn" aria-hidden="true" @click.stop="emit('delete-history', index)" />
      </div>
      <div
        v-if="hasMoreThanTwoLines"
        class="expand-btn"
        :class="{ 'is-expanded': isExpanded }"
        @click="isExpanded = !isExpanded"
      >
        <ArrowDownIcon class="arrow-icon" />
      </div>
    </div>
    <!-- 热门交易商推荐 -->
    <div v-if="!searchQuery && popularTraders.length > 0" class="section-traders-title">
      <span>{{ t('80357') }}</span>
    </div>
    <div v-if="!showSearchEmpty" class="traders-list">
      <div
        v-for="(trader, index) in popularTraders"
        :key="index"
        class="trader-item"
        @click="goTraderDetail(trader, index)"
      >
        <div class="trader-info-left">
          <div
            class="trader-logo-container"
            :style="{
              backgroundColor: trader.icon ? 'transparent' : '#25262B',
            }"
          >
            <CommonImage
              v-if="trader.icon"
              :src="trader.icon"
              alt="logo"
              class="trader-logo-img"
              loading="lazy"
              object-fit="cover"
            />
            <span v-else-if="trader.name" class="trader-logo-fallback">{{ trader.name[0].toUpperCase() }}</span>
          </div>
          <div class="trader-meta">
            <div class="trader-name" v-html="highlightMatch(trader.name, props.searchQuery)"></div>
            <div v-if="trader.status || trader.years" class="trader-tags">
              <span
                v-if="getExchangeStatus(trader)?.content"
                class="status-tag"
                :style="{ color: formatColor(getExchangeStatus(trader)?.fontColor) }"
                >{{ getExchangeStatus(trader)?.content }}</span
              >
              <span
                v-else-if="getExchangeYears(trader)?.content"
                class="status-tag"
                :style="{ color: formatColor(getExchangeYears(trader)?.fontColor) }"
                >{{ getExchangeYears(trader)?.content }}</span
              >
              <span v-if="trader.status && trader.years" class="divider-line" aria-hidden="true"></span>
              <span v-if="trader.years" class="years-text">{{ trader.years }}</span>
            </div>
          </div>
        </div>
        <div v-if="trader.score" class="trader-score-right">
          <Star class="star-icon" aria-hidden="true" />
          <span class="score-text">{{ formatScoreValue(trader.score) }}</span>
        </div>
      </div>
    </div>
    <div v-else class="search-dropdown-empty">
      <div>
        <CommonImage
          class="mx-auto block h-[130px] w-[230px]"
          src="https://resources1.interface003.com/web30/2026/05/26/85c4961e-6d37-43cb-af78-7fbdce783fe5.png"
          alt=""
          loading="lazy"
          object-fit="contain"
        />
        <div class="mt-[16px] text-[16px] text-[#292A2E]">{{ t('80359') }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.search-dropdown-wrap {
  width: 816px;
  max-height: 546px;
  position: absolute;
  top: 82px;
  left: 0;
  right: 0;
  margin: 0 auto;
  background-color: #ffffff;
  border: 1px solid rgba(5, 21, 36, 0.06);
  border-radius: 20px;
  background: #fff;
  box-shadow:
    0 0 1px 0 rgba(30, 31, 33, 0.12),
    0 0 8px 0 rgba(30, 31, 33, 0.16);
  padding: 16px 32px;
  z-index: 100;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeInDropdown 0.3s cubic-bezier(0.25, 1, 0.5, 1) both;

  .section-title {
    font-family: Outfit;
    font-size: 14px;
    font-weight: 400;
    color: #7d818a;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;

    .delete-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      width: 22px;
      height: 22px;
      justify-content: center;
      align-items: center;

      .delete-icon {
        width: 16px;
        height: 16px;
        color: #7d818a;
        transition: color 0.2s ease;
      }

      &:hover {
        background-color: #ffeceb;
        border-radius: 4px;
        .delete-icon {
          color: #ff4d4f;
        }
      }
    }
  }

  .section-traders-title {
    font-family: Outfit;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: #7d818a;
    margin-bottom: 4px;
  }

  .history-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 8px;
    margin-bottom: 16px;

    &.is-measuring {
      .tag-item {
        display: flex !important;
      }
    }

    .tag-item {
      max-width: 200px;
      background-color: #f8f8f8;
      border-radius: 16px;
      padding: 4px 12px;
      font-family: Outfit;
      font-size: 14px;
      font-weight: 400;
      color: #292a2e;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition:
        background-color 0.2s ease,
        color 0.2s ease;

      &:hover {
        background-color: #eaecef;
        color: #292a2e;
      }

      span {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .tag-icon {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .close-btn {
        margin-inline-start: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        color: #9ba0ae;
        flex-shrink: 0;
        transition: color 0.2s ease;

        &:hover {
          color: #292a2e;
        }
      }
    }

    .expand-btn {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background-color: #f8f8f8;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background-color 0.2s ease;

      .arrow-icon {
        width: 20px;
        height: 20px;
        transition: transform 0.3s ease;
      }

      &.is-expanded {
        .arrow-icon {
          transform: rotate(180deg);
        }
      }

      &:hover {
        background-color: #eaecef;
      }

      svg {
        width: 12px;
        height: 12px;
        color: #7d818a;
      }
    }
  }

  .traders-list {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    gap: 0;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    margin: 0 -32px;

    &::-webkit-scrollbar {
      display: none;
    }

    .trader-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 16px 32px;
      margin: 0;
      cursor: pointer;
      transition: background-color 0.2s ease;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 32px;
        right: 32px;
        height: 0.5px;
        background-color: var(--color-border-default, rgba(5, 21, 36, 0.06));
      }

      &:last-child::after {
        display: none;
      }

      &:hover {
        background-color: rgba(248, 248, 248, 0.72);
      }

      .trader-info-left {
        display: flex;
        align-items: center;
        flex: 1 1 0;
        min-width: 0;
        gap: 8px;

        .trader-logo-container {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;

          .trader-logo-img {
            width: 32px;
            height: 32px;
            object-fit: cover;
            border-radius: 4px;
          }

          .trader-logo-fallback {
            color: #ffffff;
            font-family: Outfit, sans-serif;
            font-size: 16px;
            font-weight: 700;
            line-height: 1;
          }
        }

        .trader-meta {
          display: flex;
          flex: 1 1 0;
          min-width: 0;
          flex-direction: column;
          gap: 2px;

          .trader-name {
            font-family: Outfit, sans-serif;
            font-size: 18px;
            font-weight: 500;
            line-height: 26px;
            color: #292a2e;
            white-space: nowrap;
          }

          .trader-tags {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: Outfit, sans-serif;
            font-size: 12px;
            line-height: 18px;

            .status-tag {
              color: #1f845a;
              font-weight: 400;
            }

            .divider-line {
              width: 1px;
              height: 8px;
              background: rgba(5, 21, 36, 0.12);
              flex-shrink: 0;
            }

            .years-text {
              color: #505258;
            }
          }
        }
      }

      .trader-score-right {
        background-color: #f8f8f8;
        border-radius: 999px;
        padding: 4px 12px;
        display: flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;

        .star-icon {
          width: 14px;
          height: 14px;
        }

        .score-text {
          color: #292a2e;
          text-align: center;
          font-family: WikiFX;
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
        }
      }
    }
  }

  .search-dropdown-empty {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
}

@keyframes fadeInDropdown {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
